import { createSlice } from "@reduxjs/toolkit";
import type { RootState } from "../app";

// Utility function to parse conversation name
const parseConversationName = (convName: string): string => {
  try {
    // Handle JSON-like string format
    if (convName.includes("'result':")) {
      const match = convName.match(/'result':\s*"([^"]+)"/);
      return match ? match[1] : convName;
    }
    return convName;
  } catch {
    return convName;
  }
};

interface I_Sources {
  page: string;
  source: string;
  text: string;
}

interface I_DbCreds {
  username: string;
  password: string;
  url: string;
}
interface i_chatMessage {
  message: string;
  type: string;
  sources?: I_Sources[];
  model?: string;
  chat_type?: string;
  org_cypher?: string;
  cypher?: string;
  db_creds?: I_DbCreds;
  image_model?: string;
  collection_chunks?: string[];
  collection_images?: string[];
  pii?: boolean;
  generated_image?: string;
  web_search?: boolean;
}

interface i_conversation {
  chat_ids: string[];
  conv_name: string;
  id: string;
  time: string;
}

// Define a conversation for the slice state
interface conversationState {
  conversation_id: string;
  chats: i_chatMessage[];
  selected_indexes: string[];
  conversations: i_conversation[];
  filteredConversations: i_conversation[];
  searchQuery: string;
  url?: string;
  username?: string;
  password?: string;
}

// Define the initial state using that conversation
const initialState: conversationState = {
  conversation_id: "",
  chats: [],
  selected_indexes: [],
  conversations: [],
  filteredConversations: [],
  searchQuery: "",
  url: "",
  username: "",
  password: "",
};

export const conversationSlice = createSlice({
  name: "conversation",
  // `createSlice` will infer the state conversation from the `initialState` argument
  initialState,
  reducers: {
    addconversation: (state, action) => {
      return {
        ...state,
        conversation_id: action.payload.conversation_id,
        chats: action.payload.chats,
      };
    },
    appendChat: (
      state,
      action: {
        payload: i_chatMessage;
      }
    ) => {
      const newChats = [...state.chats, action.payload];
      return {
        ...state,
        chats: newChats,
      };
    },
    resetChat: (state) => {
      state = initialState;
    },
    updateIndexes: (state, action) => {
      state.selected_indexes = action.payload;
    },
    addCypher: (state, action) => {
      state.url = action.payload.url;
      state.username = action.payload.username;
      state.password = action.payload.password;
    },
    addConversations: (state, action) => {
      const parsedConversations = action.payload.map(
        (conv: i_conversation) => ({
          ...conv,
          conv_name: parseConversationName(conv.conv_name),
        })
      );
      return {
        ...state,
        conversations: parsedConversations,
        filteredConversations: parsedConversations,
      };
    },
    setSearchQuery: (state, action) => {
      const query = action.payload.toLowerCase();
      state.searchQuery = query;
      state.filteredConversations = query
        ? state.conversations.filter((conv) =>
            conv.conv_name.toLowerCase().includes(query)
          )
        : state.conversations;
    },
    clearActiveChat: (state) => {
      state.conversation_id = "";
      state.chats = [];
    },
    // limitChatHistory: (state, action) => {
    //   const limit = action.payload;
    //   if (state.chats.length > limit * 2) {
    //     // Each conversation has user + gpt messages, so multiply by 2
    //     state.chats = state.chats.slice(-limit * 2);
    //   }
    // },
  },
});

export const {
  addconversation,
  appendChat,
  resetChat,
  updateIndexes,
  addConversations,
  addCypher,
  clearActiveChat,
  setSearchQuery,
  // limitChatHistory,
} = conversationSlice.actions;

// Other code such as selectors can use the imported `RootState` conversation
export const getconversationid = (state: RootState) =>
  state.conversation.conversation_id;
export const getChats = (state: RootState) => state.conversation.chats;
export const getSelectedIndexes = (state: RootState) =>
  state.conversation.selected_indexes;

export const getConversations = (state: RootState) =>
  state.conversation.conversations;
export const getFilteredConversations = (state: RootState) =>
  state.conversation.filteredConversations;
export const getSearchQuery = (state: RootState) =>
  state.conversation.searchQuery;
export const getCypherDetails = (state: RootState) => state.conversation;

export default conversationSlice.reducer;
